import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { DirectusService } from '../core/services/directus.service';

@Component({
  standalone: true,
  imports: [CommonModule],
  template: `
    <section class="flows-page">
      <header class="page-head">
        <div>
          <h1>Flows / Automation</h1>
          <p>Monitor and toggle Directus automation flows.</p>
        </div>
        <button class="btn btn-primary" (click)="load()" [disabled]="loading">Refresh</button>
      </header>

      <section class="panel">
        <div class="panel-head">
          <h2>Flow list ({{ rows.length }})</h2>
        </div>

        <p class="feedback success" *ngIf="message">{{ message }}</p>
        <p class="feedback error" *ngIf="error">{{ error }}</p>
        <p class="state" *ngIf="loading">Loading flows...</p>

        <div class="table-wrap" *ngIf="!loading">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Trigger</th>
                <th>Status</th>
                <th>Last run</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of rows">
                <td class="cell-name">{{ row['name'] || '-' }}</td>
                <td>{{ row['trigger'] || '-' }}</td>
                <td>
                  <span class="badge" [class.badge-active]="row['status'] === 'active'">
                    {{ row['status'] || '-' }}
                  </span>
                </td>
                <td>{{ row['flow_manager_last_run_at'] || '-' }}</td>
                <td>
                  <button class="btn btn-secondary" (click)="toggleStatus(row)">Toggle</button>
                </td>
              </tr>
              <tr *ngIf="rows.length === 0">
                <td colspan="5" class="state">No flows found.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </section>
  `,
  styles: [
    `
      .flows-page {
        display: grid;
        gap: 0.85rem;
        width: 100%;
        max-width: var(--app-max-width);
        margin: 0 auto;
      }

      .page-head {
        display: flex;
        justify-content: space-between;
        align-items: end;
        gap: 0.75rem;
      }

      h1 {
        margin: 0;
        font-size: var(--type-page-title);
        line-height: 1.15;
        letter-spacing: -0.02em;
        font-weight: 700;
        color: var(--app-text);
      }

      .page-head p {
        margin: 0.3rem 0 0;
        color: var(--app-muted);
        font-size: var(--type-body);
      }

      .panel {
        background: var(--app-surface);
        border: 1px solid var(--app-border);
        border-radius: var(--app-radius);
        box-shadow: var(--app-shadow);
      }

      .panel-head {
        padding: 1rem 1rem 0.6rem;
      }

      .panel-head h2 {
        margin: 0;
        font-size: var(--type-section-title);
        line-height: 1.2;
        letter-spacing: -0.01em;
        font-weight: 700;
        color: var(--app-text);
      }

      .state {
        margin: 0.25rem 1rem 0.75rem;
        color: var(--app-muted);
      }

      .btn {
        border-radius: 999px;
        padding: 0.38rem 0.85rem;
        font-weight: 700;
        border: 1px solid var(--app-border);
        background: var(--app-surface);
        color: var(--app-primary);
      }

      .btn:disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }

      .btn-primary {
        border-color: var(--app-primary);
        background: var(--app-primary);
        color: #fff;
      }

      .btn-secondary {
        border-color: #99d5ee;
        background: #f2fbff;
        color: var(--app-primary);
      }

      .feedback {
        margin: 0 1rem 0.75rem;
        border-radius: 8px;
        padding: 0.55rem 0.7rem;
        font-size: 0.9rem;
      }

      .feedback.success {
        background: #e8f9ef;
        color: #0f6a3a;
      }

      .feedback.error {
        background: #ffe9ea;
        color: #b3261e;
      }

      .table-wrap {
        overflow: auto;
        border-top: 1px solid var(--app-border);
      }

      table {
        width: 100%;
        border-collapse: collapse;
        min-width: 860px;
      }

      th {
        border-bottom: 1px solid var(--app-border);
        padding: 0.72rem 0.85rem;
        text-align: left;
        background: #d9ecf8;
        color: var(--app-text);
        font-size: 0.84rem;
        font-weight: 700;
      }

      td {
        border-bottom: 1px solid var(--app-border);
        padding: 0.72rem 0.85rem;
        color: var(--app-text);
        font-size: 0.95rem;
        vertical-align: middle;
      }

      tbody tr:last-child td {
        border-bottom: none;
      }

      .cell-name {
        font-weight: 700;
      }

      .badge {
        display: inline-flex;
        align-items: center;
        border-radius: 999px;
        padding: 0.14rem 0.54rem;
        border: 1px solid #ffd2d2;
        background: #fff1f1;
        color: #9a2b2b;
        text-transform: capitalize;
        font-size: 0.78rem;
        font-weight: 700;
        letter-spacing: 0.01em;
      }

      .badge-active {
        border-color: #b8e9cc;
        background: #e8f8ee;
        color: #0f6a3a;
      }
    `
  ]
})
export class FlowsComponent implements OnInit {
  rows: Record<string, unknown>[] = [];
  loading = true;
  error = '';
  message = '';

  constructor(private readonly directus: DirectusService) {}

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    this.error = '';

    try {
      this.rows = await this.directus.listFlows({
        limit: 100,
        sort: 'name',
        fields: 'id,name,status,trigger,description,flow_manager_last_run_at'
      });
    } catch (error: unknown) {
      this.error = error instanceof Error ? error.message : 'Failed to load flows';
    } finally {
      this.loading = false;
    }
  }

  async toggleStatus(row: Record<string, unknown>): Promise<void> {
    const id = String(row['id'] || '');
    if (!id) return;

    const currentStatus = String(row['status'] || 'inactive');
    const nextStatus = currentStatus === 'active' ? 'inactive' : 'active';

    this.error = '';
    this.message = '';

    try {
      await this.directus.updateFlow(id, { status: nextStatus });
      this.message = `${String(row['name'] || 'Flow')} is now ${nextStatus}.`;
      await this.load();
    } catch (error: unknown) {
      this.error = error instanceof Error ? error.message : 'Failed to update flow status';
    }
  }
}
